/**
 * Utility functions for handling profile pictures from Google/Gmail
 */

/**
 * Get a high-quality Google profile picture URL
 * @param {string} originalUrl - The original Google profile picture URL
 * @param {number} size - Desired size (default: 200)
 * @returns {string} - Optimized profile picture URL
 */
export const getOptimizedGoogleProfilePicture = (originalUrl, size = 200) => {
  if (!originalUrl) return null;
  
  // If it's already a Google profile picture URL, optimize it
  if (originalUrl.includes('googleusercontent.com') || originalUrl.includes('ggpht.com')) {
    // Remove existing size parameters and add our own
    const baseUrl = originalUrl.split('=')[0];
    return `${baseUrl}=s${size}-c`;
  }
  
  return originalUrl;
};

/**
 * Generate Gravatar URL from email as fallback
 * @param {string} email - User's email
 * @param {number} size - Desired size (default: 200)
 * @returns {string} - Gravatar URL
 */
export const getGravatarUrl = (email, size = 200) => {
  if (!email) return null;
  
  // Create MD5 hash of email (simplified version)
  const hash = btoa(email.toLowerCase().trim()).replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=identicon`;
};

/**
 * Get the best available profile picture URL
 * @param {Object} user - User object
 * @param {number} size - Desired size (default: 200)
 * @returns {string|null} - Best available profile picture URL
 */
export const getBestProfilePicture = (user, size = 200) => {
  if (!user) return null;
  
  // First try Google profile picture
  if (user.profilePhoto) {
    const optimized = getOptimizedGoogleProfilePicture(user.profilePhoto, size);
    if (optimized) return optimized;
  }
  
  // Fallback to Gravatar if we have an email
  if (user.email) {
    return getGravatarUrl(user.email, size);
  }
  
  return null;
};

/**
 * Check if a profile picture URL is valid
 * @param {string} url - Profile picture URL
 * @returns {Promise<boolean>} - Whether the URL is valid
 */
export const isValidProfilePictureUrl = async (url) => {
  if (!url) return false;
  
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok && response.headers.get('content-type')?.startsWith('image/');
  } catch (error) {
    console.warn('Profile picture URL validation failed:', error);
    return false;
  }
};

/**
 * Refresh user's Google profile picture
 * @param {string} googleId - User's Google ID
 * @returns {Promise<string|null>} - Updated profile picture URL
 */
export const refreshGoogleProfilePicture = async (googleId) => {
  if (!googleId) return null;
  
  try {
    // This would require Google People API access
    // For now, we'll return null and rely on the login flow to update
    console.log('Profile picture refresh requested for Google ID:', googleId);
    return null;
  } catch (error) {
    console.error('Failed to refresh Google profile picture:', error);
    return null;
  }
};